/**
 * SwapMealDrawer
 * -------------
 * Slide-up drawer that lets the user swap a meal in their plan.
 *
 * Flow:
 *  1. Open → shows current meal name + "Get 4 alternatives" button
 *  2. AI generates 4 ideas (meal_swap action) — same style as FlyerDeals
 *  3. User taps a card → full recipe loads (quick_recipe action, same as FlyerDeals)
 *  4. User confirms → swapMealInPlan() replaces the slot silently
 *  5. Drawer closes with a success toast
 */
import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Sparkles, Loader2, Clock, Flame, Globe, DollarSign,
  Users, Check, RefreshCw, ArrowRight, Leaf,
} from 'lucide-react';
import { useAppStore } from '@/hooks/useAppStore';
import { Meal } from '@/lib/index';

const EDGE_FUNCTION_URL = 'https://pgjncthsfxsylmvkgibx.supabase.co/functions/v1/meal-plan';
const ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBnam5jdGhzZnhzeWxtdmtnaWJ4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcxMDEwMTEsImV4cCI6MjA5MjY3NzAxMX0.PDtIhTOuZNnwOqNkdOD8eTC6SCXdlPT5H7vrcU30UXk';

// ── Types ─────────────────────────────────────────────────────────────────────
interface MealIdea {
  name: string;
  tagline: string;
  cook_time: string;
  method: string;
  cuisine: string;
  difficulty: 'Easy' | 'Medium' | 'Weekend Project';
}
interface QuickRecipeIngredient {
  name: string; qty: string; est_price: number; on_sale: boolean;
}
interface QuickRecipe {
  name: string; serves: number; cook_time: string;
  est_cost: number; cost_per_serving: number;
  ingredients: QuickRecipeIngredient[];
  instructions: string[];
  tip?: string;
}

const DIFFICULTY_META: Record<string, { cls: string }> = {
  'Easy':             { cls: 'text-green-400 bg-green-500/15' },
  'Medium':           { cls: 'text-yellow-400 bg-yellow-500/15' },
  'Weekend Project':  { cls: 'text-orange-400 bg-orange-500/15' },
};

// ── Props ─────────────────────────────────────────────────────────────────────
interface SwapMealDrawerProps {
  meal: Meal;
  storeName: string;
  onClose: () => void;
  onSwapped: (newName: string) => void; // called after successful swap
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function SwapMealDrawer({ meal, storeName, onClose, onSwapped }: SwapMealDrawerProps) {
  const { swapMealInPlan } = useAppStore();

  // Main protein = first on-sale ingredient, or first ingredient
  const mainIngredient = meal.ingredients.find(i => i.on_sale) ?? meal.ingredients[0];
  const proteinName  = mainIngredient?.name  ?? '';
  const proteinPrice = Number(mainIngredient?.price ?? 0);

  const [ideas, setIdeas]               = useState<MealIdea[]>([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [fetched, setFetched]           = useState(false);
  const [openCard, setOpenCard]         = useState<number | null>(null);
  const [recipeLoading, setRecipeLoading] = useState<number | null>(null);
  const [recipes, setRecipes]           = useState<Record<number, QuickRecipe>>({});
  const [confirming, setConfirming]     = useState<number | null>(null);

  // ── Fetch swap ideas ────────────────────────────────────────────────────────
  const fetchIdeas = useCallback(async () => {
    setLoading(true); setError(''); setIdeas([]); setRecipes({}); setOpenCard(null);
    try {
      const res = await fetch(EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${ANON_KEY}`, 'apikey': ANON_KEY },
        body: JSON.stringify({
          action: 'meal_swap',
          currentMealName: meal.name,
          proteinName,
          proteinPrice,
          mealType: meal.meal_type,
          storeName,
        }),
      });
      const data = await res.json() as { ideas?: MealIdea[]; error?: string };
      if (data.error) throw new Error(data.error);
      setIdeas(data.ideas ?? []);
      setFetched(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load alternatives');
    } finally {
      setLoading(false);
    }
  }, [meal.name, meal.meal_type, proteinName, proteinPrice, storeName]);

  // ── Fetch full recipe on tap ────────────────────────────────────────────────
  const fetchRecipe = useCallback(async (idea: MealIdea, idx: number) => {
    if (recipes[idx]) { setOpenCard(idx); return; }
    setRecipeLoading(idx); setOpenCard(idx);
    try {
      const res = await fetch(EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${ANON_KEY}`, 'apikey': ANON_KEY },
        body: JSON.stringify({
          action: 'quick_recipe',
          recipeName: idea.name,
          proteinName,
          proteinPrice,
          cuisine: idea.cuisine,
          method: idea.method,
        }),
      });
      const data = await res.json() as { recipe?: QuickRecipe; error?: string };
      if (data.error) throw new Error(data.error);
      if (data.recipe) setRecipes(prev => ({ ...prev, [idx]: data.recipe! }));
    } catch (e) {
      console.error('quick_recipe swap failed:', e);
    } finally {
      setRecipeLoading(null);
    }
  }, [proteinName, proteinPrice, recipes]);

  // ── Confirm swap ────────────────────────────────────────────────────────────
  const confirmSwap = (idea: MealIdea, recipe: QuickRecipe, idx: number) => {
    setConfirming(idx);
    // Build a proper Meal object from the QuickRecipe
    const newMeal: Meal = {
      id: meal.id, // will be overwritten by swapMealInPlan to keep same id
      name: recipe.name,
      tagline: idea.tagline,
      meal_type: meal.meal_type,
      day: meal.day,
      serves: recipe.serves ?? 4,
      cook_time: recipe.cook_time,
      method: idea.method,
      ingredients: recipe.ingredients.map(i => ({
        name: i.name,
        qty: i.qty,
        price: i.est_price ?? 0,
        on_sale: i.on_sale ?? false,
      })),
      total_cost: recipe.est_cost ?? 0,
      per_serving: recipe.cost_per_serving ?? 0,
      instructions: recipe.instructions ?? [],
      tip: recipe.tip ?? '',
      sides: [],
      photo_url: undefined,
    };
    swapMealInPlan(meal.id, newMeal);
    setTimeout(() => {
      setConfirming(null);
      onSwapped(recipe.name);
      onClose();
    }, 600);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Backdrop */}
      <motion.div
        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      />

      {/* Drawer */}
      <motion.div
        className="fixed bottom-0 left-0 right-0 z-50 max-w-lg mx-auto bg-card rounded-t-3xl border-t border-border shadow-2xl"
        style={{ maxHeight: '92vh', overflowY: 'auto' }}
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
      >
        {/* Sticky header */}
        <div className="sticky top-0 bg-card rounded-t-3xl px-5 pt-4 pb-3 border-b border-border z-10">
          <div className="w-10 h-1 bg-muted rounded-full mx-auto mb-4" />
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">
                🔀 Swap Meal · {meal.day}
              </p>
              <h2 className="font-black text-foreground text-base leading-tight line-clamp-2">
                {meal.name}
              </h2>
              {proteinName && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Main protein: <span className="text-primary font-semibold">{proteinName}</span>
                  {proteinPrice > 0 && <span className="text-primary font-semibold"> · ${proteinPrice.toFixed(2)}</span>}
                </p>
              )}
            </div>
            <button onClick={onClose}
              className="w-8 h-8 rounded-xl bg-muted flex items-center justify-center shrink-0">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        <div className="px-5 py-5 pb-10 space-y-4">

          {/* CTA — first open */}
          {!fetched && !loading && (
            <button onClick={fetchIdeas}
              className="w-full py-3.5 rounded-2xl bg-primary text-white font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-lg shadow-primary/30">
              <Sparkles className="w-4 h-4" /> Generate 4 Alternatives
            </button>
          )}

          {/* Loading ideas */}
          {loading && (
            <div className="flex flex-col items-center py-10 gap-4">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/25 flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-primary animate-spin" />
              </div>
              <div className="text-center">
                <p className="font-semibold text-foreground text-sm">Finding alternatives…</p>
                <p className="text-xs text-muted-foreground mt-1">AI is generating 4 options for {proteinName || 'this protein'}</p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="text-center py-6">
              <p className="text-red-400 text-sm font-medium mb-3">{error}</p>
              <button onClick={fetchIdeas} className="text-sm text-primary font-semibold">Try Again</button>
            </div>
          )}

          {/* Ideas list */}
          {fetched && !loading && ideas.length > 0 && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                  Tap a card to preview the recipe
                </p>
                {/* Re-roll button */}
                <button onClick={fetchIdeas}
                  className="flex items-center gap-1 text-xs text-primary font-semibold hover:opacity-80 transition-opacity">
                  <RefreshCw className="w-3 h-3" /> Re-roll
                </button>
              </div>

              <div className="space-y-3">
                {ideas.map((idea, i) => {
                  const diff    = DIFFICULTY_META[idea.difficulty] ?? DIFFICULTY_META['Easy'];
                  const recipe  = recipes[i];
                  const isOpen  = openCard === i;
                  const isLoadingRecipe = recipeLoading === i;
                  const isConfirming = confirming === i;

                  return (
                    <motion.div key={i}
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.06 }}
                      className={`rounded-2xl border overflow-hidden transition-all ${
                        isOpen ? 'border-primary/50 shadow-md shadow-primary/10 bg-card' : 'border-border bg-muted/20'
                      }`}
                    >
                      {/* Card header — tap to expand */}
                      <button
                        onClick={() => isOpen ? setOpenCard(null) : fetchRecipe(idea, i)}
                        className="w-full text-left p-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-foreground text-sm leading-snug">{idea.name}</h3>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{idea.tagline}</p>
                          </div>
                          <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${diff.cls}`}>
                            {idea.difficulty}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                            <Clock className="w-3 h-3" />{idea.cook_time}
                          </span>
                          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                            <Flame className="w-3 h-3" />{idea.method}
                          </span>
                          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                            <Globe className="w-3 h-3" />{idea.cuisine}
                          </span>
                          {recipe && (
                            <span className="flex items-center gap-1 text-[11px] font-bold text-primary ml-auto">
                              <DollarSign className="w-3 h-3" />
                              ~${recipe.cost_per_serving.toFixed(2)}/srv
                            </span>
                          )}
                        </div>
                      </button>

                      {/* Loading recipe */}
                      {isOpen && isLoadingRecipe && (
                        <div className="border-t border-border/50 px-4 py-5 flex items-center justify-center gap-3">
                          <Loader2 className="w-5 h-5 text-primary animate-spin" />
                          <span className="text-sm text-muted-foreground">Building recipe…</span>
                        </div>
                      )}

                      {/* Full recipe preview */}
                      <AnimatePresence>
                        {isOpen && !isLoadingRecipe && recipe && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}
                            className="overflow-hidden"
                          >
                            <div className="border-t border-border/50 px-4 pb-4 pt-3 space-y-4 bg-card">

                              {/* Summary bar */}
                              <div className="grid grid-cols-4 divide-x divide-border/60 bg-muted/30 rounded-xl overflow-hidden border border-border/40">
                                {[
                                  { icon: <Users className="w-3 h-3" />, top: `${recipe.serves ?? 4}`, bot: 'Serves' },
                                  { icon: <Clock className="w-3 h-3" />, top: recipe.cook_time, bot: 'Time' },
                                  { icon: null, top: `$${recipe.cost_per_serving.toFixed(2)}`, bot: '/srv' },
                                  { icon: null, top: `$${recipe.est_cost.toFixed(2)}`, bot: 'Total' },
                                ].map((item, j) => (
                                  <div key={j} className="flex flex-col items-center py-2 px-1 gap-0.5">
                                    {item.icon && <span className="text-primary">{item.icon}</span>}
                                    <span className={`text-xs font-black ${j >= 2 ? 'text-primary' : 'text-foreground'}`}>{item.top}</span>
                                    <span className="text-[9px] uppercase tracking-wide text-muted-foreground">{item.bot}</span>
                                  </div>
                                ))}
                              </div>

                              {/* Ingredients preview */}
                              <div>
                                <div className="flex items-center gap-1.5 mb-2">
                                  <div className="w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                                    <Leaf className="w-2.5 h-2.5 text-white" />
                                  </div>
                                  <p className="text-[10px] font-black text-primary uppercase tracking-wider">Ingredients</p>
                                </div>
                                <div className="space-y-1">
                                  {recipe.ingredients.map((ing, j) => (
                                    <div key={j} className="flex items-center justify-between gap-2">
                                      <div className="flex items-center gap-1.5 min-w-0">
                                        {ing.on_sale && <span className="text-[9px] text-primary font-bold shrink-0">SALE</span>}
                                        <span className={`text-xs leading-snug ${ing.on_sale ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                                          {ing.qty} {ing.name}
                                        </span>
                                      </div>
                                      {ing.est_price > 0 && (
                                        <span className="text-[10px] font-bold text-muted-foreground shrink-0">
                                          ~${ing.est_price.toFixed(2)}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Instructions preview (first 3 steps) */}
                              <div>
                                <div className="flex items-center gap-1.5 mb-2">
                                  <div className="w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                                    <Leaf className="w-2.5 h-2.5 text-white" />
                                  </div>
                                  <p className="text-[10px] font-black text-primary uppercase tracking-wider">Instructions</p>
                                </div>
                                <div className="space-y-2">
                                  {recipe.instructions.slice(0, 3).map((step, j) => (
                                    <div key={j} className="flex gap-2.5">
                                      <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center shrink-0 mt-0.5">
                                        <span className="text-[10px] font-black text-white">{j + 1}</span>
                                      </div>
                                      <p className="text-xs text-foreground leading-relaxed">{step}</p>
                                    </div>
                                  ))}
                                  {recipe.instructions.length > 3 && (
                                    <p className="text-[10px] text-muted-foreground pl-7">
                                      + {recipe.instructions.length - 3} more steps after swap
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Confirm swap button */}
                              <button
                                onClick={() => confirmSwap(idea, recipe, i)}
                                disabled={!!confirming}
                                className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-lg ${
                                  isConfirming
                                    ? 'bg-green-500 text-white shadow-green-500/30'
                                    : 'bg-primary text-white shadow-primary/30 hover:opacity-90'
                                }`}
                              >
                                {isConfirming
                                  ? <><Check className="w-4 h-4" /> Swapped!</>
                                  : <><ArrowRight className="w-4 h-4" /> Use This Recipe</>
                                }
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            </>
          )}

        </div>
      </motion.div>
    </>
  );
}
